import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getOptimizedImageUrl } from '../utils/image'

const CACHE_STORAGE_KEY = 'ol_home_cache_v2'

function loadFromStorage() {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function parseBooleanSetting(val, defaultVal = false) {
  if (val === undefined || val === null || val === '') return defaultVal
  if (typeof val === 'boolean') return val
  if (val === 'true' || val === 1 || val === '1') return true
  if (val === 'false' || val === 0 || val === '0') return false
  return defaultVal
}

export const useHomeStore = defineStore('home', () => {
  const isLoaded = ref(false)
  const lastFetched = ref(0)
  const loading = ref(false)

  // Hero & Slides
  const slides = ref([])
  const bentoCards = ref([])
  const trustBadges = ref([])
  const lifestyleItems = ref([])
  const secondaryCategories = ref([])

  // Section titles
  const sectionTitles = ref({
    showcaseTitle: 'สินค้าแนะนำพิเศษ',
    showcaseHeading: 'สินค้าเพื่อพื้นที่ของคุณ',
    categoriesTitle: 'หมวดหมู่สินค้า',
    categoriesHeading: 'เลือกหมวดหมู่ที่ต้องการ',
    projectsTitle: 'ผลงานการติดตั้งจริง',
    projectsHeading: 'ความไว้วางใจจากลูกค้าทั่วประเทศ',
    articlesTitle: 'บทความและสาระน่ารู้',
    articlesHeading: 'เคล็ดลับการดูแลและเลือกซื้อ',
    affiliatesTitle: 'พันธมิตรทางธุรกิจ',
    affiliatesHeading: 'บริษัทในเครือและพันธมิตรของเรา',
    faqTitle: 'คำถามที่พบบ่อย',
    faqHeading: 'ข้อสงสัยเกี่ยวกับสินค้าและบริการ'
  })

  // Showcase & Categories
  const showcaseProducts = ref([])
  const categories = ref([])
  const categoryShowcaseSettings = ref([])
  const showcaseCategoriesData = ref([])
  const homeHighlightSettings = ref(null)
  const highlightCategoriesList = ref([])
  const homeShowcaseSlider = ref([])

  // Content sections
  const recentProjects = ref([])
  const recentArticles = ref([])
  const affiliatedCompanies = ref([])
  const partners = ref([])
  const testimonials = ref([])
  const stats = ref([])
  const featuresItems = ref([])
  const howItWorksSteps = ref([])
  const faqItems = ref([])

  // Features texts
  const featuresHeading = ref('')
  const featuresTitle = ref('')
  const featuresDesc = ref('')
  const featuresImage = ref('')
  const featuresBadgeTitle = ref('')
  const featuresBadgeDesc = ref('')

  // CTA texts
  const ctaTitle = ref('')
  const ctaDesc = ref('')
  const ctaButtonText = ref('')
  const ctaButtonLink = ref('')

  // Section visibility toggles
  const homeShowTestimonials = ref(false)
  const homeShowPartners = ref(false)
  const homeShowAffiliates = ref(false)
  const homeShowFaq = ref(false)
  const homeShowShowcase = ref(true)
  const homeShowStats = ref(false)
  const homeShowFeatures = ref(false)
  const homeShowHowItWorks = ref(false)

  // Synchronously hydrate from cache on store init so on refresh (F5),
  // configured slides and settings render immediately with 0ms flash!
  const cached = loadFromStorage()
  if (cached && typeof cached === 'object') {
    if (Array.isArray(cached.slides)) slides.value = cached.slides
    if (Array.isArray(cached.bentoCards)) bentoCards.value = cached.bentoCards
    if (Array.isArray(cached.trustBadges)) trustBadges.value = cached.trustBadges
    if (Array.isArray(cached.lifestyleItems)) lifestyleItems.value = cached.lifestyleItems
    if (Array.isArray(cached.secondaryCategories)) secondaryCategories.value = cached.secondaryCategories
    if (Array.isArray(cached.partners)) partners.value = cached.partners
    if (Array.isArray(cached.testimonials)) testimonials.value = cached.testimonials
    if (Array.isArray(cached.stats)) stats.value = cached.stats
    if (Array.isArray(cached.featuresItems)) featuresItems.value = cached.featuresItems
    if (Array.isArray(cached.howItWorksSteps)) howItWorksSteps.value = cached.howItWorksSteps
    if (Array.isArray(cached.faqItems)) faqItems.value = cached.faqItems
    if (cached.featuresHeading) featuresHeading.value = cached.featuresHeading
    if (cached.featuresTitle) featuresTitle.value = cached.featuresTitle
    if (cached.featuresDesc) featuresDesc.value = cached.featuresDesc
    if (cached.featuresImage) featuresImage.value = cached.featuresImage
    if (cached.featuresBadgeTitle) featuresBadgeTitle.value = cached.featuresBadgeTitle
    if (cached.featuresBadgeDesc) featuresBadgeDesc.value = cached.featuresBadgeDesc
    if (cached.ctaTitle) ctaTitle.value = cached.ctaTitle
    if (cached.ctaDesc) ctaDesc.value = cached.ctaDesc
    if (cached.ctaButtonText) ctaButtonText.value = cached.ctaButtonText
    if (cached.ctaButtonLink) ctaButtonLink.value = cached.ctaButtonLink
    if (cached.homeShowTestimonials !== undefined) homeShowTestimonials.value = cached.homeShowTestimonials
    if (cached.homeShowPartners !== undefined) homeShowPartners.value = cached.homeShowPartners
    if (cached.homeShowAffiliates !== undefined) homeShowAffiliates.value = cached.homeShowAffiliates
    if (cached.homeShowFaq !== undefined) homeShowFaq.value = cached.homeShowFaq
    if (cached.homeShowShowcase !== undefined) homeShowShowcase.value = cached.homeShowShowcase
    if (cached.homeShowStats !== undefined) homeShowStats.value = cached.homeShowStats
    if (cached.homeShowFeatures !== undefined) homeShowFeatures.value = cached.homeShowFeatures
    if (cached.homeShowHowItWorks !== undefined) homeShowHowItWorks.value = cached.homeShowHowItWorks
    if (Array.isArray(cached.showcaseCategoriesData) && cached.showcaseCategoriesData.length > 0) showcaseCategoriesData.value = cached.showcaseCategoriesData
    if (Array.isArray(cached.showcaseProducts) && cached.showcaseProducts.length > 0) showcaseProducts.value = cached.showcaseProducts
    if (Array.isArray(cached.categories) && cached.categories.length > 0) categories.value = cached.categories
    if (Array.isArray(cached.recentProjects)) recentProjects.value = cached.recentProjects
    if (Array.isArray(cached.recentArticles)) recentArticles.value = cached.recentArticles
    if (Array.isArray(cached.affiliatedCompanies)) affiliatedCompanies.value = cached.affiliatedCompanies
    if (cached.homeHighlightSettings) homeHighlightSettings.value = cached.homeHighlightSettings
    if (Array.isArray(cached.highlightCategoriesList)) highlightCategoriesList.value = cached.highlightCategoriesList
    if (Array.isArray(cached.homeShowcaseSlider)) homeShowcaseSlider.value = cached.homeShowcaseSlider
    if (cached.sectionTitles) sectionTitles.value = { ...sectionTitles.value, ...cached.sectionTitles }
    isLoaded.value = true
    lastFetched.value = cached.savedAt || Date.now()
  }

  const saveCache = () => {
    if (typeof window === 'undefined') return
    try {
      const payload = {
        savedAt: Date.now(),
        slides: slides.value,
        bentoCards: bentoCards.value,
        trustBadges: trustBadges.value,
        lifestyleItems: lifestyleItems.value,
        secondaryCategories: secondaryCategories.value,
        sectionTitles: sectionTitles.value,
        showcaseProducts: showcaseProducts.value,
        categories: categories.value,
        categoryShowcaseSettings: categoryShowcaseSettings.value,
        showcaseCategoriesData: showcaseCategoriesData.value,
        homeHighlightSettings: homeHighlightSettings.value,
        highlightCategoriesList: highlightCategoriesList.value,
        homeShowcaseSlider: homeShowcaseSlider.value,
        recentProjects: recentProjects.value,
        recentArticles: recentArticles.value,
        affiliatedCompanies: affiliatedCompanies.value,
        partners: partners.value,
        testimonials: testimonials.value,
        stats: stats.value,
        featuresItems: featuresItems.value,
        howItWorksSteps: howItWorksSteps.value,
        faqItems: faqItems.value,
        featuresHeading: featuresHeading.value,
        featuresTitle: featuresTitle.value,
        featuresDesc: featuresDesc.value,
        featuresImage: featuresImage.value,
        featuresBadgeTitle: featuresBadgeTitle.value,
        featuresBadgeDesc: featuresBadgeDesc.value,
        ctaTitle: ctaTitle.value,
        ctaDesc: ctaDesc.value,
        ctaButtonText: ctaButtonText.value,
        ctaButtonLink: ctaButtonLink.value,
        homeShowTestimonials: homeShowTestimonials.value,
        homeShowPartners: homeShowPartners.value,
        homeShowAffiliates: homeShowAffiliates.value,
        homeShowFaq: homeShowFaq.value,
        homeShowShowcase: homeShowShowcase.value,
        homeShowStats: homeShowStats.value,
        homeShowFeatures: homeShowFeatures.value,
        homeShowHowItWorks: homeShowHowItWorks.value
      }
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {}
  }

  const fetchHomeData = async (force = false) => {
    // 2-minute cache validity unless forced
    if (!force && isLoaded.value && (Date.now() - lastFetched.value < 120000)) {
      return
    }

    loading.value = true
    try {
      // 1. Fetch settings, categories, products, projects, and articles in parallel
      const [settingsRes, categoriesRes, productsRes, projectsRes, articlesRes] = await Promise.all([
        fetch('/api/settings').then(r => r.json()).catch(() => ({})),
        fetch('/api/categories').then(r => r.json()).catch(() => ({ data: [] })),
        fetch('/api/products').then(r => r.json()).catch(() => ({ data: [] })),
        fetch('/api/projects/published?limit=4').then(r => r.json()).catch(() => ({ data: [] })),
        fetch('/api/articles/published?limit=3').then(r => r.json()).catch(() => ({ data: [] }))
      ])

      const s = settingsRes.data || settingsRes.settings || {}
      const allProds = Array.isArray(productsRes.data) ? productsRes.data : []
      const rawCategories = Array.isArray(categoriesRes.data) ? categoriesRes.data : []

      // 2. Parse Homepage Settings
      if (s.home_slides) {
        try {
          const parsed = JSON.parse(s.home_slides)
          slides.value = (Array.isArray(parsed) && parsed.length > 0) ? parsed : []
        } catch (e) {
          slides.value = []
        }
      } else {
        slides.value = []
      }

      // Parse JSON settings
      try {
        if (s.home_bento_cards) {
          const parsedBento = JSON.parse(s.home_bento_cards)
          if (Array.isArray(parsedBento) && parsedBento.length > 0) {
            bentoCards.value = parsedBento
          }
        }
      } catch (e) {}

      try {
        if (s.home_trust_badges) {
          const parsedTrust = JSON.parse(s.home_trust_badges)
          if (Array.isArray(parsedTrust) && parsedTrust.length > 0) {
            trustBadges.value = parsedTrust
          }
        }
      } catch (e) {}

      try {
        if (s.home_lifestyle_items) {
          const parsedLife = JSON.parse(s.home_lifestyle_items)
          if (Array.isArray(parsedLife) && parsedLife.length > 0) {
            lifestyleItems.value = parsedLife
          }
        }
      } catch (e) {}

      try {
        if (s.home_secondary_categories) {
          const parsedSec = JSON.parse(s.home_secondary_categories)
          if (Array.isArray(parsedSec) && parsedSec.length > 0) {
            secondaryCategories.value = parsedSec
          }
        }
      } catch (e) {}

      try { 
        const parsedStats = s.home_stats ? JSON.parse(s.home_stats) : null
        if (parsedStats && Array.isArray(parsedStats) && parsedStats.length > 0) stats.value = parsedStats 
      } catch (e) {}
      try { 
        const parsedFeatures = s.home_features_items ? JSON.parse(s.home_features_items) : null
        if (parsedFeatures && Array.isArray(parsedFeatures) && parsedFeatures.length > 0) featuresItems.value = parsedFeatures 
      } catch (e) {}
      try { 
        const parsedTestis = s.home_testimonials ? JSON.parse(s.home_testimonials) : null
        if (parsedTestis && Array.isArray(parsedTestis)) {
          testimonials.value = parsedTestis.map(t => ({
            name: t.name || '',
            role: t.role || '',
            comment: t.comment || t.text || '',
            rating: Number(t.rating || t.stars || 5),
            avatar: t.avatar || ''
          }))
        } else {
          testimonials.value = []
        }
      } catch (e) {}
      try { 
        const parsedPartners = s.home_partners ? JSON.parse(s.home_partners) : null
        if (parsedPartners && Array.isArray(parsedPartners)) {
          partners.value = parsedPartners.map(p => ({
            name: p.name || '',
            logo: p.logo || p.logo_url || '',
            link: p.link || '#'
          }))
        } else {
          partners.value = []
        }
      } catch (e) {}
      try { 
        const parsedAffiliates = s.home_affiliates ? JSON.parse(s.home_affiliates) : []
        affiliatedCompanies.value = Array.isArray(parsedAffiliates) ? parsedAffiliates : []
      } catch (e) {}
      try { 
        const parsedShowcase = s.home_category_showcase ? JSON.parse(s.home_category_showcase) : []
        categoryShowcaseSettings.value = Array.isArray(parsedShowcase) ? parsedShowcase : []
      } catch (e) {}
      try { 
        const parsedShowcaseSlider = s.home_showcase_slider ? JSON.parse(s.home_showcase_slider) : []
        homeShowcaseSlider.value = Array.isArray(parsedShowcaseSlider) ? parsedShowcaseSlider : []
      } catch (e) {}
      try { 
        const parsedHighlights = s.home_highlight_categories ? JSON.parse(s.home_highlight_categories) : null
        homeHighlightSettings.value = (parsedHighlights && typeof parsedHighlights === 'object') ? parsedHighlights : null
      } catch (e) {}
      try {
        const parsedFaq = s.home_faq ? JSON.parse(s.home_faq) : null
        if (parsedFaq && parsedFaq.length > 0) faqItems.value = parsedFaq
      } catch (e) {}
      try {
        const parsedHowItWorks = s.home_how_it_works ? JSON.parse(s.home_how_it_works) : null
        if (parsedHowItWorks && parsedHowItWorks.length > 0) howItWorksSteps.value = parsedHowItWorks
      } catch (e) {}
      try {
        const parsedTitles = s.home_section_titles ? JSON.parse(s.home_section_titles) : null
        if (parsedTitles && Object.keys(parsedTitles).length > 0) sectionTitles.value = { ...sectionTitles.value, ...parsedTitles }
      } catch (e) {}

      // Text settings
      featuresHeading.value = s.home_features_heading || ''
      featuresTitle.value = s.home_features_title || ''
      featuresDesc.value = s.home_features_desc || ''
      const rawFeatImg = s.home_features_image || ''
      featuresImage.value = rawFeatImg ? getOptimizedImageUrl(rawFeatImg) : ''
      featuresBadgeTitle.value = s.home_features_badge_title || ''
      featuresBadgeDesc.value = s.home_features_badge_desc || ''

      ctaTitle.value = s.home_cta_title || ''
      ctaDesc.value = s.home_cta_desc || ''
      ctaButtonText.value = s.home_cta_button_text || ''
      ctaButtonLink.value = s.home_cta_button_link || ''

      // Section visibility toggles
      homeShowTestimonials.value = parseBooleanSetting(s.home_show_testimonials, false)
      homeShowPartners.value = parseBooleanSetting(s.home_show_partners, false)
      homeShowAffiliates.value = parseBooleanSetting(s.home_show_affiliates, false)
      homeShowFaq.value = parseBooleanSetting(s.home_show_faq, false)
      homeShowShowcase.value = parseBooleanSetting(s.home_show_showcase, true)
      homeShowStats.value = parseBooleanSetting(s.home_show_stats, false)
      homeShowFeatures.value = parseBooleanSetting(s.home_show_features, false)
      homeShowHowItWorks.value = parseBooleanSetting(s.home_show_how_it_works, false)

      // 3. Process Categories
      const priority = [
        'บ้านเก็บของพลาสติก PP',
        'บ้านเก็บของพลาสติก HDPE',
        'บ้านเก็บของโลหะ',
        'โรงเรือนปลูกพืช',
        'กล่องเก็บของกลางแจ้ง'
      ]
      const sortedCategories = [...rawCategories].sort((a, b) => {
        const indexA = priority.indexOf(a.name)
        const indexB = priority.indexOf(b.name)
        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return 0
      })
      categories.value = sortedCategories

      // 4. Process Showcase Products
      showcaseProducts.value = allProds.slice(0, 6).map((p) => ({
        id: p.id,
        slug: p.slug,
        sku: p.sku,
        title: p.name,
        image: p.image_url || '/images/placeholder.png',
        subtitle: p.sku ? `รุ่น ${p.sku}` : (p.category || 'Premium Unit'),
        price: p.price,
        originalPrice: p.original_price,
        is_out_of_stock: p.is_out_of_stock || false,
        isBestSeller: false,
        isHiddenMobile: '',
        card_features: p.card_features || null,
        compare_enabled: p.compare_enabled !== false
      }))

      if (homeShowcaseSlider.value && homeShowcaseSlider.value.length > 0) {
        homeShowcaseSlider.value = homeShowcaseSlider.value.map(slide => {
          const product = slide.productId ? allProds.find(p => p.id === parseInt(slide.productId)) : null
          return { ...slide, product }
        })
      }

      // 5. Build Category Showcase Blocks
      if (categoryShowcaseSettings.value.length > 0 && sortedCategories.length > 0) {
        const formattedShowcase = []
        for (const showcase of categoryShowcaseSettings.value) {
          const category = sortedCategories.find(c => 
            String(c.id) === String(showcase.categoryId) || 
            String(c.name).trim().toLowerCase() === String(showcase.categoryId).trim().toLowerCase()
          )
          if (!category) continue

          let products = []
          const pinnedIds = Array.isArray(showcase.productIds) ? showcase.productIds : []

          if (pinnedIds.length > 0) {
            pinnedIds.forEach(pid => {
              const found = allProds.find(p => String(p.id) === String(pid))
              if (found && !products.some(p => String(p.id) === String(found.id))) {
                products.push(found)
              }
            })
          } else {
            const catIdStr = String(category.id)
            const catName = String(category.name).trim().toLowerCase()
            const catProducts = allProds.filter(p => {
              if (p.category !== undefined && p.category !== null) {
                const pCatStr = String(p.category).trim().toLowerCase()
                if (pCatStr === catIdStr || pCatStr === catName) return true
              }
              if (p.category_name && String(p.category_name).trim().toLowerCase() === catName) return true
              if (Array.isArray(p.categories)) {
                if (p.categories.some(c => String(c).trim().toLowerCase() === catIdStr || String(c).trim().toLowerCase() === catName)) return true
              }
              return false
            })
            catProducts.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || b.id - a.id)
            products = catProducts.slice(0, 4)
          }

          if (products.length === 0) continue

          let parsedFeatures = { enabled: true, items: [] }
          try {
            if (typeof category.features === 'string') {
              const parsed = JSON.parse(category.features)
              if (Array.isArray(parsed)) {
                parsedFeatures.items = parsed || []
              } else if (parsed && typeof parsed === 'object') {
                parsedFeatures.enabled = parsed.enabled !== false
                parsedFeatures.items = Array.isArray(parsed.items) ? parsed.items : []
              }
            } else if (Array.isArray(category.features)) {
              parsedFeatures.items = [...category.features]
            } else if (category.features && typeof category.features === 'object') {
              parsedFeatures.enabled = category.features.enabled !== false
              parsedFeatures.items = Array.isArray(category.features.items) ? category.features.items : []
            }
          } catch (e) {}

          formattedShowcase.push({
            category: { ...category, features: parsedFeatures },
            products: products.map(p => ({
              ...p,
              id: p.id,
              slug: p.slug,
              sku: p.sku,
              name: p.name,
              title: p.name,
              image: p.image_url || '/images/placeholder.png',
              image_url: p.image_url || '/images/placeholder.png',
              subtitle: p.sku ? `รุ่น: ${p.sku}` : (p.category || 'Premium Unit'),
              price: p.price,
              originalPrice: p.original_price,
              original_price: p.original_price,
              is_out_of_stock: p.is_out_of_stock || false,
              card_features: p.card_features || null,
              compare_enabled: p.compare_enabled !== false
            }))
          })
        }
        showcaseCategoriesData.value = formattedShowcase
      }

      // 6. Highlight Categories
      if (homeHighlightSettings.value && homeHighlightSettings.value.items && homeHighlightSettings.value.items.some(i => i.categoryId)) {
        const configuredCats = homeHighlightSettings.value.items
          .map(item => {
            const cat = sortedCategories.find(c => String(c.id) === String(item.categoryId))
            if (!cat) return null
            return {
              ...cat,
              customDescription: item.customDesc || cat.description,
              productIds: item.productIds || []
            }
          })
          .filter(Boolean)

        const usedIds = configuredCats.map(c => String(c.id))
        let fallbackIndex = 0
        while (configuredCats.length < 5 && fallbackIndex < sortedCategories.length) {
          const fallbackCat = sortedCategories[fallbackIndex]
          if (!usedIds.includes(String(fallbackCat.id))) {
            configuredCats.push({
              ...fallbackCat,
              customDescription: fallbackCat.description,
              productIds: []
            })
            usedIds.push(String(fallbackCat.id))
          }
          fallbackIndex++
        }
        highlightCategoriesList.value = configuredCats
      } else {
        highlightCategoriesList.value = sortedCategories.slice(0, 5).map(cat => ({
          ...cat,
          customDescription: cat.description,
          productIds: []
        }))
      }

      // 7. Recent Content
      if (projectsRes.success && Array.isArray(projectsRes.data)) {
        recentProjects.value = projectsRes.data.slice(0, 4)
      }
      if (articlesRes.success && Array.isArray(articlesRes.data)) {
        recentArticles.value = articlesRes.data.slice(0, 3)
      }

      isLoaded.value = true
      lastFetched.value = Date.now()
      saveCache()
    } catch (err) {
      console.error('Failed to fetch home store data:', err)
    } finally {
      loading.value = false
    }
  }

  return {
    isLoaded,
    loading,
    slides,
    bentoCards,
    trustBadges,
    lifestyleItems,
    secondaryCategories,
    sectionTitles,
    showcaseProducts,
    categories,
    categoryShowcaseSettings,
    showcaseCategoriesData,
    homeHighlightSettings,
    highlightCategoriesList,
    homeShowcaseSlider,
    recentProjects,
    recentArticles,
    affiliatedCompanies,
    partners,
    testimonials,
    stats,
    featuresItems,
    howItWorksSteps,
    faqItems,
    featuresHeading,
    featuresTitle,
    featuresDesc,
    featuresImage,
    featuresBadgeTitle,
    featuresBadgeDesc,
    ctaTitle,
    ctaDesc,
    ctaButtonText,
    ctaButtonLink,
    homeShowTestimonials,
    homeShowPartners,
    homeShowAffiliates,
    homeShowFaq,
    homeShowShowcase,
    homeShowStats,
    homeShowFeatures,
    homeShowHowItWorks,
    saveCache,
    fetchHomeData
  }
})
